/**
 * Walk the rooms (owner, 2026-10-09: "you setup most of the app for them and then hand it off … the main rooms can get AI to
 * catalog the soap, the towels … and setup the inventory partially"; "walk through and then acknowledge all the things already in
 * the place and hand it off"). Three stages, in these words:
 *
 *   1. Walk through: pick or add a room, take 1–6 photos (WALK_PHOTOS_PER_ROOM). Each photo is a setup scan of kind "room" (what is here, by TYPE:
 *      paper towels, hand soap, a reach-in, a hand sink, a handwash sign) or "label" (a product label or a nameplate: brand,
 *      product, size, make, model, serial, copied verbatim, only when legible). The models PROPOSE; nothing is saved here.
 *   2. Acknowledge: "You have: …" first, one tap "That's right" per room (FRAMEWORK T8: only what a person taps is saved), then
 *      the short "To add" list ("What the inspector looks for here"), cited from the place's state pack.
 *   3. Hand it off: "Your place is set up: 4 rooms, 23 supplies …, set up by <name> on <date>", the map, the Easy Wins, and the
 *      next step (take it over when someone else set it up; else "Start your first check").
 *
 * Rules (each has a test in packages/core/test/roomwalk.test.ts):
 *  W1 Type first. A room photo names TYPES from WALK_ITEMS only. Brand, product, size, make, model and serial come only from a
 *     label photo, and only words that appear in that photo's verbatim reading (`verbatimOnly`); anything else is blank, never
 *     a guess. A blank brand says "Brand not readable".
 *  W2 Nothing applied without a tap: `ackRoom` takes only the items a person sent.
 *  W3 Rules come from the state pack: every "To add" and Easy Win cites through `ruleAt` (placecode.ts codeAt, or the pack's own
 *     adoption line, or the pack's posting), never a state's words written here. Where the state's text differs, it says so.
 *  W4 No judgement words in anything we write for the owner (JUDGEMENT_WORDS; lint test). "To add", never "Violation".
 *  W5 A spotted issue the model flags is a suggestion until the person walking confirms it; only confirmed ones reach Easy Wins.
 *  W6 Idempotent: acknowledging a room again recomputes its counts; it never doubles a supply's on-hand.
 */
import { SUPPLY_SEEDS, type Supply, type SupplyKind } from "./actions.ts";
import type { Id, IsoDateTime, Location } from "./model.ts";
import { citesFoodCodeItself, codeAt, differsLine, type PlaceRef } from "./placecode.ts";
import { packFor, stateOrDefault } from "./states/index.ts";
import type { StorageSpot } from "./spots.ts";
import { SECTION_MAX, SECTIONS_MAX } from "./spots.ts";
import { AHEAD } from "./ahead.ts";
import type { SetupScan } from "./setupscan.ts";
import type { AheadThing } from "./isthisyou.ts";
import { mergeRoomRows, type WalkRow } from "./walkmerge.ts";
import { cleanObjectName, objectGuideFor, OTHER_NAME_MAX, OTHER_TYPE, plainName, type ObjectGuide } from "./walkobjects.ts";
import { counterName } from "./walkcounters.ts";
import { prepProgress, walkCompletion, type PrepProgress, type WalkCompletion } from "./walkchecklist.ts";
import { caseGuide, floorFixId, floorGuides, WALK_FLOORS, GROUP_TITLE, KIND_ITEMS, materialChoices, materialOf, OLD_KEY_KINDS, seatGuide, UNITS_PER_ROW_MAX, WALK_GROUPS, type WalkGroup } from "./walkkinds.ts";

/* ─────────────── rooms ─────────────── */

export type WalkRoomKind = "dining" | "front" | "bar" | "restroom" | "kitchen" | "dish" | "storage" | "walkin" | "patio" | "other";
export interface WalkRoomDef { kind: WalkRoomKind; name: string; /** What to take a photo of, one plain line. */ look: string; /** Rooms a setter-up usually walks (the owner finishes the kitchen). */ setter: boolean }

export const WALK_ROOM_DEFS: readonly WalkRoomDef[] = [
  { kind: "dining", name: "Dining room", look: "The tables, high chairs and any condiment or self-serve station.", setter: true },
  { kind: "patio", name: "Patio", look: "The outdoor tables and chairs, umbrellas, trash and recycling, bird and pest signs, and any outdoor bar or service station.", setter: false },
  { kind: "front", name: "Front counter", look: "The pastry or display case, the sneeze guard and the tongs.", setter: true },
  { kind: "bar", name: "Bar", look: "The machines (espresso, ice), the hand sink and what sits by it.", setter: true },
  { kind: "restroom", name: "Restroom", look: "The hand sink with the soap, towels and sign, and the toilet paper.", setter: true },
  { kind: "storage", name: "Storage", look: "The shelves: soap, towels, gloves, sanitizer and test strips.", setter: true },
  { kind: "walkin", name: "Walk-in", look: "The cooler door, its thermometer and the shelves.", setter: true },
  { kind: "dish", name: "Dish area", look: "The 3-compartment sink or dish machine, test strips and the hand sink.", setter: false },
  { kind: "kitchen", name: "Kitchen", look: "The line, fridges, hand sink and thermometers.", setter: false },
  { kind: "other", name: "Other room", look: "What's kept here.", setter: false },
];
export const roomDef = (k: WalkRoomKind): WalkRoomDef => WALK_ROOM_DEFS.find((d) => d.kind === k) ?? WALK_ROOM_DEFS[WALK_ROOM_DEFS.length - 1]!;
export const isWalkRoomKind = (v: unknown): v is WalkRoomKind => typeof v === "string" && WALK_ROOM_DEFS.some((d) => d.kind === v);
export const WALK_ROOMS_MAX = 20;

/** One-tap names for "Add a room" (owner, 2026-10-15: one place has a patio). The kind is what the guides and look lines follow; the name is the person's. */
export const WALK_ROOM_SUGGESTIONS: readonly { name: string; kind: WalkRoomKind }[] = [
  { name: "Dining room", kind: "dining" }, { name: "Patio", kind: "patio" }, { name: "Bar", kind: "bar" }, { name: "Restroom", kind: "restroom" },
  { name: "Kitchen", kind: "kitchen" }, { name: "Storage", kind: "storage" }, { name: "Walk-in", kind: "walkin" }, { name: "Dish area", kind: "dish" },
  { name: "Front counter", kind: "front" }, { name: "Office", kind: "other" }, { name: "Hallway", kind: "other" },
];
const KIND_WORDS: readonly [WalkRoomKind, RegExp][] = [
  ["patio", /\b(patio|outdoor|outdoors|outside|deck|terrace|courtyard|beer garden|garden|porch)\b/],
  ["restroom", /\b(restrooms?|bathrooms?|toilets?|washrooms?|lavatory|men|mens|women|womens|ladies|gents)\b/],
  ["walkin", /\b(walk in|walkin|cooler|freezer|cold room)\b/],
  ["dish", /\b(dish|dishes|dishwashing|warewashing|ware wash)\b/],
  ["kitchen", /\b(kitchen|prep|cook line|line)\b/],
  ["storage", /\b(storage|stockroom|stock room|pantry|dry goods|supply|supplies)\b/],
  ["front", /\b(front counter|counter|register|cashier|order)\b/],
  ["bar", /\b(bar|espresso|coffee bar)\b/],
  ["dining", /\b(dining|dine|seating|lobby|main room)\b/],
];
/** The kind a typed room name is closest to ("Back deck" → patio, "Men's" → restroom); anything else is "other". The typed name is kept. */
export function roomKindForName(name: string): WalkRoomKind {
  const n = name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, " ").trim();
  if (!n) return "other";
  const exact = WALK_ROOM_SUGGESTIONS.find((x) => x.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim() === n);
  if (exact) return exact.kind;
  return KIND_WORDS.find(([, re]) => re.test(n))?.[0] ?? "other";
}
/** Room photos per room (owner, 2026-10-13: "Yes more than three" for burst photos). Each is one AI read, under the daily cap. */
export const WALK_PHOTOS_PER_ROOM = 6;
export const WALK_ROOM_NAME_MAX = 40;
/** A room's name as kept (walkobjects.ts plainName, at most WALK_ROOM_NAME_MAX): the walk, the owner's review and the preview link. */
export const cleanRoomName = (v: unknown): string => plainName(v).slice(0, WALK_ROOM_NAME_MAX);

/* ─────────────── what a room can hold, by type (W1) ─────────────── */

export type WalkItemKind = "supply" | "equipment" | "sign" | "furniture";
export interface WalkItemType {
  key: string;
  label: string;
  kind: WalkItemKind;
  /** The supply it is (actions.ts SUPPLY_SEEDS key), for supplies we track. */
  supplyKey?: string;
  /** A supply we don't seed: its kind when it is added to the order list. */
  supplyKind?: SupplyKind;
  /** The Things library type (objectops-data.ts) a piece of equipment becomes. */
  thingTypeId?: string;
  coldUnit?: true;
  handSink?: true;
  /** Kinds on the walk (walkkinds.ts): floor, table, seat, case or fridge. */
  group?: WalkGroup;
  /** Its words in the binder line, one and many ("two-top", "two-tops"). */
  short?: [string, string];
  /** A case that is cold, hot or neither unless the person says otherwise. */
  holdingDefault?: Holding;
  /** One plain line the walk shows under it (the prep-top's rail). */
  note?: string;
}

const sup = (key: string, label: string, supplyKey = key): WalkItemType => ({ key, label, kind: "supply", supplyKey });
const eq = (key: string, label: string, thingTypeId?: string, extra: Partial<WalkItemType> = {}): WalkItemType => ({ key, label, kind: "equipment", ...(thingTypeId ? { thingTypeId } : {}), ...extra });

export const WALK_ITEMS: readonly WalkItemType[] = /* @__PURE__ */ (() => ([
  sup("hand-soap", "Hand soap"), sup("paper-towels", "Paper towels"), sup("toilet-paper", "Toilet paper"), sup("trash-bags", "Trash bags"),
  sup("gloves", "Single-use gloves"), sup("sanitizer", "Food-contact sanitizer"), sup("test-strips", "Sanitizer test strips"),
  sup("wiping-cloths", "Wiping cloths"), sup("dish-soap", "Dish soap"), sup("disinfectant", "Restroom disinfectant"), sup("bleach", "Unscented bleach"),
  sup("glass-cleaner", "Glass cleaner"), sup("degreaser", "Degreaser"),
  eq("espresso-machine", "Espresso machine", "espresso-machine"), eq("grinder", "Coffee grinder", "grinder"), eq("batch-brewer", "Batch brewer", "batch-brewer"),
  eq("ice-machine", "Ice machine", "ice-machine"), eq("dish-machine", "Dish machine", "dish-machine-high-temp"), eq("fryer", "Fryer", "fryer"),
  eq("oven", "Oven", "convection-oven"), eq("range", "Range", "commercial-range"), eq("griddle", "Griddle", "griddle"),
  eq("reach-in-fridge", "Reach-in refrigerator", "reach-in-fridge", { coldUnit: true }), eq("walk-in", "Walk-in cooler", "walk-in", { coldUnit: true }),
  eq("freezer", "Freezer", "freezer", { coldUnit: true }), eq("walk-in-freezer", "Walk-in freezer", "walk-in-freezer", { coldUnit: true }),
  eq("display-case", "Display case", "display-case"), eq("hand-sink", "Handwashing sink", "hand-sink", { handSink: true }),
  eq("three-compartment-sink", "Three-compartment sink", "three-compartment-sink"), eq("prep-sink", "Prep sink"), eq("mop-sink", "Mop sink", "service-sink"),
  eq("food-thermometer", "Food thermometer", "probe-thermometer"), eq("cooler-thermometer", "Thermometer inside a cooler"), eq("hand-dryer", "Hand dryer"),
  eq("sanitizer-bucket", "Sanitizer bucket", "sanitizer-bucket"), eq("insect-light-trap", "Insect light trap"), eq("trash-can", "Trash can", "trash-can"),
  eq("sneeze-guard", "Sneeze guard"), eq("tongs", "Tongs and serving tools"), eq("urinal", "Urinal"),
  { key: "handwash-sign", label: "Handwash sign", kind: "sign" },
  { key: "dining-table", label: "Dining tables", kind: "furniture" }, { key: "high-chair", label: "High chairs", kind: "furniture" },
  { key: "condiment-station", label: "Condiment station", kind: "furniture" }, { key: "self-serve-station", label: "Self-serve station", kind: "furniture" },
  // Something not on this list (walkobjects.ts O1–O4): its own name, the nearest guide or the general rule. Never dropped.
  { key: OTHER_TYPE, label: "Something else", kind: "furniture" },
] as WalkItemType[]).map((i): WalkItemType => (OLD_KEY_KINDS[i.key] ? { ...i, ...OLD_KEY_KINDS[i.key] } : i)).concat(KIND_ITEMS))();
export const WALK_ITEM_KEYS: readonly string[] = /* @__PURE__ */ (() => WALK_ITEMS.map((i) => i.key))();
export const walkItem = (key: string | undefined | null): WalkItemType | undefined => (key ? WALK_ITEMS.find((i) => i.key === key) : undefined);

const foldName = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, " ").trim();
/**
 * A name that is one of our kinds (walkobjects.ts O1): "Ice machine", "high chairs", "trash can" → that kind's key; else null. Only the
 * whole name against a kind's label or its words ("two-top", "high chairs"), so "host stand" is never a "stand mixer".
 */
export function walkTypeForName(name: string): string | null {
  const n = foldName(name);
  if (!n) return null;
  for (const t of WALK_ITEMS) {
    if (t.key === OTHER_TYPE) continue;
    const names = [t.label, ...(t.short ?? [])].map(foldName);
    if (names.includes(n) || names.includes(n.replace(/s$/, "")) || names.some((x) => x === `${n}s`)) return t.key;
  }
  return null;
}

/** A display case holds food hot, cold, or neither. */
export const HOLDING = ["hot", "cold", "none"] as const;
export type Holding = (typeof HOLDING)[number];

type KindBits = { typeKey: string; holding?: Holding | null; material?: string | null; drain?: boolean | null; guideId?: string | null };
/** A case's cold, hot or neither: what the person said, else the kind's default (walkkinds.ts K3); a fridge is cold. */
export function holdingOf(i: KindBits): Holding | undefined {
  const t = walkItem(i.typeKey);
  if (t?.group === "case") return i.holding ?? t.holdingDefault;
  return t?.coldUnit ? "cold" : undefined;
}
/** Kept at 41°F or colder (or frozen) and checked each day: a fridge, a freezer, a cold case. */
export const isColdUnit = (i: KindBits): boolean => !!walkItem(i.typeKey)?.coldUnit || (walkItem(i.typeKey)?.group === "case" && holdingOf(i) === "cold");
/** Kept at 135°F or hotter and checked each day: a hot case. */
export const isHotUnit = (i: KindBits): boolean => walkItem(i.typeKey)?.group === "case" && holdingOf(i) === "hot";


/** The guides (Things library type ids) an item becomes (walkkinds.ts K4): by its floor, material or holding; else its type's own. */
export function thingTypesFor(i: KindBits, room: WalkRoomKind): string[] {
  const t = walkItem(i.typeKey);
  if (!t) return [];
  if (t.group === "floor") return floorGuides(i.material, i.drain, room);
  if (t.group === "table") return ["surface:dining-tables"];
  if (t.group === "counter") return ["surface:prep-counter"]; // surfaces.ts "Food prep counters and tables" (its sourced steps)
  if (t.group === "seat") return [seatGuide(t.key, i.material)];
  if (t.group === "case") { const g = caseGuide(holdingOf(i)); return g ? [g] : []; }
  if (t.key === OTHER_TYPE) return i.guideId ? [i.guideId] : []; // walkobjects.ts O1; no guide, no Thing (O2)
  return t.thingTypeId && t.kind === "equipment" ? [t.thingTypeId] : [];
}

/* ─────────────── verbatim only (W1) ─────────────── */

const foldText = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, " ").trim();

/**
 * `value` when every word of it appears, in order, in the photo's verbatim reading; else null. A brand the model "knows" but the
 * photo doesn't show is dropped: never guessed.
 */
export function verbatimOnly(value: unknown, readText: string): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim().slice(0, 120);
  const f = foldText(v);
  if (!f) return null;
  return ` ${foldText(readText)} `.includes(` ${f} `) ? v : null;
}

/** A serial number shown in lists: the last 4 only ("••••4821"). Stored whole; shown whole only on its own thing's page. */
export function maskSerial(serial: string | undefined | null): string | undefined {
  const s = (serial ?? "").trim();
  if (!s) return undefined;
  return s.length <= 4 ? "••••" : `••••${s.slice(-4)}`;
}
export const BRAND_NOT_READABLE = "Brand not readable";
export const MODEL_NOT_READABLE = "Model not readable";

/* ─────────────── the walk ─────────────── */

export type WalkRoomStatus = "to_walk" | "walking" | "done";
export const WALK_STATUS_WORD: Record<WalkRoomStatus, string> = { to_walk: "To walk", walking: "Walking", done: "Done" };

/** One thing a person said "That's right" to: the starting inventory and the binder's "What's in place". */
export interface AckItem {
  id: string;
  typeKey: string;
  kind: WalkItemKind;
  label: string;
  count?: number;
  unit?: string;
  /** Where in the room ("at the handwash sink"). */
  where?: string;
  holding?: Holding;
  /** Kinds (walkkinds.ts): the floor type, a table's top or a seat's material, from that group's choices. */
  material?: string;
  /** A floor: is there a floor drain. */
  drain?: boolean;
  /** From a label photo, or typed by the person; never a model's guess. */
  brand?: string;
  product?: string;
  size?: string;
  make?: string;
  model?: string;
  serial?: string;
  capacity?: string;
  voltage?: string;
  /** The supply key it filled, the thing it made. */
  supplyKey?: string;
  thingId?: string;
  /** Every thing it made, when more than one (2 under-counter fridges, a floor and its drain); thingId is the first. */
  thingIds?: string[];
  /** The setup scan (photo) it came from. */
  scanId?: string;
  par?: number;
  /** Make it yours (walkreview.ts): a label or nameplate photo the owner attached to this item (one of the room's labelScanIds). */
  photoScanId?: string;
  /** Make it yours: each change after hand-off, who and when, before → after. The person who set it up stays on the room. */
  changes?: ItemChange[];
  /** Make it yours: an item the owner added after hand-off (it was missed). */
  addedBy?: WalkStamp;
  /** Something not on our list (walkobjects.ts): the guide its name maps to, decided by core from the name, never sent by a client. */
  guideId?: string;
  guideName?: string;
  /** R1: the name the photos gave, when the person renamed it (walkchecklist.ts): a later read of the same thing still finds it. */
  readAs?: string;
  /** The counter it sits on, by name (walkcounters.ts N3). */
  onCounter?: string;
  /** The owner renamed it on their preview link (previewtouch.ts): the name it had before. Their name then leads its line. */
  ownerNamedFrom?: string;
}

/** "Not here" (walkchecklist.ts L2): a row the photos suggested that the person said isn't here; `ids` are the photo items it stood on. */
export interface NotHere { typeKey: string; label: string; ids?: string[] }

/** Who did something and when, with the name shown on the record. */
export interface WalkStamp { at: IsoDateTime; by: Id; byName: string }
/** One audited change to one field of an item: "brand: Purell → Lysol, by Rosa on Oct 10". */
export interface ItemChange extends WalkStamp { field: string; from: string | number | null; to: string | number | null }

export type IssueStatus = "suggested" | "confirmed" | "dismissed";
/** Something the model saw that an inspector may look at (W5): hidden from the owner until the person walking confirms it. */
export interface SpottedIssue { id: string; fixId: string; evidence: string; confidence: number; scanId: string; status: IssueStatus; at?: IsoDateTime; by?: Id }

export interface WalkRoom {
  key: string;
  kind: WalkRoomKind;
  name: string;
  status: WalkRoomStatus;
  scanIds: string[];
  /** Label and nameplate photos of this room. */
  labelScanIds?: string[];
  spotId: string;
  acknowledged?: { at: IsoDateTime; by: Id; byName: string; items: AckItem[]; /** L2: rows the person said are not here. */ notHere?: NotHere[] };
  issues: SpottedIssue[];
  /** Easy Wins (keys) the person walking hid from the owner. */
  hidden?: string[];
  /** Make it yours (walkreview.ts): items the owner removed (kept, so Undo puts them back and nothing is lost). */
  removedItems?: (AckItem & { removed: WalkStamp })[];
  /** Room photos taken out (a retake or a remove): kept on file, never shown. */
  removedScanIds?: string[];
  /** Photo changes after hand-off: retake, add, remove (counted on the team site, never shown there). */
  photoLog?: (WalkStamp & { kind: "retake" | "add" | "remove" | "restore"; scanId: string; replaced?: string })[];
  /** The room's name before the owner renamed it. */
  renamedFrom?: string;
  /** P1: one of the rooms a preparer finishes (the Dining room, the Men's restroom). */
  focus?: true;
  /** A room the owner added after hand-off. */
  addedBy?: WalkStamp;
  /** Copy this room (roomcopy.ts): where this room was copied from, and the things it starts with as "To check" (no counts, spots, serials or photos). Cleared by "That's right". */
  copiedFrom?: WalkStamp & { key: string; name: string };
  toCheck?: AckInput[];
  /** Walk autosave (walkdraft.ts D3): what the person did to this room's rows so far, kept on the server so no device, tab or reload loses it. */
  draft?: import("./walkdraft.ts").RoomDraft;
  /** Earlier "That's right" lists by someone else, kept when a room is walked again (nothing anyone confirmed is overwritten). */
  priorAcks?: { at: IsoDateTime; by: Id; byName: string; items: AckItem[] }[];
}

/** Make it yours: the owner's review of what someone else set up (walkreview.ts). */
export interface WalkReview {
  startedAt: IsoDateTime;
  by: Id;
  byName: string;
  /** Rooms the owner said "This room is right" to. */
  checked: Record<string, WalkStamp>;
  dismissedAt?: IsoDateTime;
  finishedAt?: IsoDateTime;
}

export interface RoomWalk {
  id: Id;
  orgId: Id;
  locationId: Id;
  startedAt: IsoDateTime;
  startedBy: Id;
  startedByName: string;
  rooms: WalkRoom[];
  /** Set when a CleanCleaver staff member preps the business for its owner (prep.ts). */
  prep?: { staffPersonId: string; staffName: string; at: IsoDateTime };
  handedOffAt?: IsoDateTime;
  handedOffBy?: Id;
  /** The rooms exactly as handed off (taken at hand-off): what the staff member and the preview link may still see. */
  setUp?: { at: IsoDateTime; rooms: WalkRoom[] };
  /** Rooms the owner removed in the review (kept, so Undo puts them back). */
  removedRooms?: (WalkRoom & { removed: WalkStamp; index: number })[];
  review?: WalkReview;
  /** Walks the team merged into this one (teamsite/movewalk.ts): a second move of one of them answers "already merged". */
  mergedFrom?: { walkId: Id; orgId: Id; at: IsoDateTime }[];
  updatedAt: IsoDateTime;
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "room";
export const roomSpotId = (roomKey: string) => `spot-walk-${roomKey}`;

/** A new room with a unique key ("restroom", then "restroom-2"). */
export function newRoom(walk: Pick<RoomWalk, "rooms">, kind: WalkRoomKind, name?: string): WalkRoom {
  const n = cleanRoomName(name) || roomDef(kind).name;
  const base = slug(n);
  let key = base, i = 2;
  while (walk.rooms.some((r) => r.key === key)) key = `${base}-${i++}`;
  return { key, kind, name: n, status: "to_walk", scanIds: [], spotId: roomSpotId(key), issues: [] };
}

/** The walk a setter-up starts with: the rooms they usually do; the kitchen stays "To walk" for the owner. */
export function newWalk(o: { id: Id; orgId: Id; locationId: Id; at: IsoDateTime; by: Id; byName: string; rooms?: (WalkRoomKind | { kind: WalkRoomKind; name: string; focus?: true; key?: string })[] }): RoomWalk {
  const w: RoomWalk = { id: o.id, orgId: o.orgId, locationId: o.locationId, startedAt: o.at, startedBy: o.by, startedByName: o.byName, rooms: [], updatedAt: o.at };
  for (const k of o.rooms ?? ["dining", "front", "restroom", "storage", "kitchen"]) {
    const made = typeof k === "string" ? newRoom(w, k) : { ...newRoom(w, k.kind, k.name), ...(k.focus ? { focus: true as const } : {}) };
    // A room may keep a key that links and records already use (the Men's restroom is "restroom").
    const room = typeof k !== "string" && k.key && !w.rooms.some((r) => r.key === k.key) ? { ...made, key: k.key, spotId: roomSpotId(k.key) } : made;
    w.rooms.push(room);
  }
  return w;
}

/** Move a room up or down (the map's order). */
export function moveRoom(rooms: WalkRoom[], key: string, dir: -1 | 1): WalkRoom[] {
  const i = rooms.findIndex((r) => r.key === key);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= rooms.length) return rooms;
  const out = [...rooms];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

/** The room to continue from: the first one not done (resume after a partial walk). */
export const nextRoom = (w: Pick<RoomWalk, "rooms">): WalkRoom | undefined => w.rooms.find((r) => r.status === "walking") ?? w.rooms.find((r) => r.status !== "done");

/* ─────────────── starting par (suggested, never a rule) ─────────────── */

export const PAR_LINE = "Suggested starting point: what's here now, more for a bigger team. Change it any time.";
/** What to keep at least: the count seen (1 when not counted), doubled for a team of 6 or more. A suggestion, labeled so. */
export function suggestPar(count: number | undefined | null, people: number): number {
  const base = typeof count === "number" && Number.isFinite(count) && count >= 1 ? Math.round(count) : 1;
  return Math.min(999, people >= 6 ? base * 2 : base);
}

/* ─────────────── acknowledge a room (W2, W6) ─────────────── */

export interface AckInput {
  typeKey: string;
  count?: number | null;
  unit?: string | null;
  where?: string | null;
  holding?: Holding | null;
  material?: string | null;
  drain?: boolean | null;
  brand?: string | null; product?: string | null; size?: string | null;
  make?: string | null; model?: string | null; serial?: string | null; capacity?: string | null; voltage?: string | null;
  par?: number | null;
  scanId?: string | null;
  label?: string | null;
  /** R1: the name the photos gave, when the person renamed it. */
  readAs?: string | null;
  /** The counter it sits on, by name (walkcounters.ts N3). */
  onCounter?: string | null;
  /** L3: a close-up of THIS object: one of the room's own photos (checked in ackRoom). */
  photoScanId?: string | null;
}

const clip = (v: unknown, max: number): string | undefined => { const s = plainName(v); return s ? s.slice(0, max) : undefined; };
/** Where an item sits ("around the table and by the window"). Was clipped at a shelf name's 30 letters, mid-word ("by the wi"). */
export const WHERE_MAX = 80;
/** Clip at a word boundary, never mid-word. */
const clipWords = (v: unknown, max: number): string | undefined => {
  const t = clip(v, 10_000);
  if (!t || t.length <= max) return t;
  const cut = t.slice(0, max + 1), sp = cut.lastIndexOf(" ");
  return (sp > max / 2 ? cut.slice(0, sp) : t.slice(0, max)).replace(/[\s,;:–-]+$/, "");
};
const intIn = (v: unknown, max: number): number | undefined => (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= max ? Math.round(v) : undefined);

/** A person's item list from a client, cleaned: unknown types are refused (never silently dropped). */
export function cleanAckInputs(v: unknown): { ok: true; items: AckInput[] } | { ok: false; message: string } {
  if (!Array.isArray(v)) return { ok: false, message: '"items" must be a list of what you saw.' };
  if (v.length > 120) return { ok: false, message: "At most 120 items per room." };
  const items: AckInput[] = [];
  for (const [i, x] of v.entries()) {
    if (!x || typeof x !== "object") return { ok: false, message: `Item ${i + 1} isn't readable.` };
    const o = x as Record<string, unknown>;
    const t = walkItem(o.typeKey as string);
    if (!t) return { ok: false, message: `Item ${i + 1} is not a type we know ("${String(o.typeKey).slice(0, 40)}").` };
    if (t.key === OTHER_TYPE && !cleanObjectName(o.label)) return { ok: false, message: `Item ${i + 1}: say what it is (a name, like "Host stand").` };
    const holding = HOLDING.includes(o.holding as Holding) ? (o.holding as Holding) : undefined;
    const material = o.material === null || o.material === undefined || o.material === "" ? undefined : materialOf(t.group, String(o.material))?.key;
    if (o.material !== null && o.material !== undefined && o.material !== "" && !material) return { ok: false, message: `${t.label}: pick one of ${materialChoices(t.group).map((m) => m.label.toLowerCase()).join(", ") || "nothing (it has no material)"}.` };
    items.push({
      typeKey: o.typeKey as string, count: intIn(o.count, 9999) ?? null, unit: clip(o.unit, 40) ?? null, where: clipWords(o.where, WHERE_MAX) ?? null, holding: holding ?? null,
      material: material ?? null, drain: t.group === "floor" && typeof o.drain === "boolean" ? o.drain : null,
      brand: clip(o.brand, 80) ?? null, product: clip(o.product, 120) ?? null, size: clip(o.size, 40) ?? null,
      make: clip(o.make, 80) ?? null, model: clip(o.model, 80) ?? null, serial: clip(o.serial, 60) ?? null, capacity: clip(o.capacity, 40) ?? null, voltage: clip(o.voltage, 40) ?? null,
      par: intIn(o.par, 999) ?? null, scanId: clip(o.scanId, 80) ?? null, label: (t.key === OTHER_TYPE ? cleanObjectName(o.label) : clip(o.label, 80)) || null,
      readAs: clip(o.readAs, OTHER_NAME_MAX) ?? null, onCounter: clip(o.onCounter, OTHER_NAME_MAX) ?? null, photoScanId: clip(o.photoScanId, 80) ?? null,
    });
  }
  return { ok: true, items };
}

/** The rows a person said are not here (walkchecklist.ts L2), cleaned: known types only, short names, at most 120. */
export function cleanNotHere(v: unknown): NotHere[] {
  if (!Array.isArray(v)) return [];
  const out: NotHere[] = [];
  for (const x of v.slice(0, 120)) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    if (!walkItem(o.typeKey as string)) continue;
    const ids = Array.isArray(o.ids) ? o.ids.map((i) => clip(i, 80)).filter((i): i is string => !!i).slice(0, 20) : [];
    out.push({ typeKey: o.typeKey as string, label: cleanObjectName(o.label) || walkItem(o.typeKey as string)!.label, ...(ids.length ? { ids } : {}) });
  }
  return out;
}

export interface ThingPlan { typeId: string; name: string; room: string; make?: string; model?: string; serial?: string; capacity?: string; voltage?: string; ackId: string; existingId?: string }

export interface AckResult {
  room: WalkRoom;
  /** Location.supplies after (only when it changed). */
  supplies?: Supply[];
  /** Location.storageSpots after (only when it changed). */
  spots?: StorageSpot[];
  /** Equipment to create (or update, `existingId`) on Things. */
  things: ThingPlan[];
  items: AckItem[];
}

/**
 * "That's right" for one room: the items a person sent become the room's acknowledged list, supplies (by type, brand and size
 * when given, kept in the room's spot, on-hand = the counts across every room, par suggested when none is set), equipment for
 * Things, and the room's spot. Pure; the server writes what it returns.
 */
export function ackRoom(o: {
  walk: RoomWalk; roomKey: string; inputs: AckInput[]; location: Pick<Location, "supplies" | "storageSpots">;
  things: readonly { id: string; typeId: string; room: string; name?: string; removed?: boolean }[]; at: IsoDateTime; by: Id; byName: string; people: number; newId: (i: number) => string;
  /** L2: the rows the person said are not here. */ notHere?: readonly NotHere[];
}): AckResult {
  const room = o.walk.rooms.find((r) => r.key === o.roomKey);
  if (!room) throw new Error(`No room ${o.roomKey}`);
  const items: AckItem[] = o.inputs.map((x, i) => {
    const t = walkItem(x.typeKey)!;
    const it: AckItem = { id: o.newId(i), typeKey: t.key, kind: t.kind, label: x.label ?? t.label };
    const set = <K extends keyof AckItem>(k: K, v: AckItem[K] | null | undefined) => { if (v !== null && v !== undefined && v !== "") it[k] = v; };
    set("count", x.count ?? undefined); set("unit", x.unit ?? undefined); set("where", x.where ?? undefined); set("holding", x.holding ?? undefined);
    set("material", x.material ?? undefined); if (typeof x.drain === "boolean") it.drain = x.drain;
    set("brand", x.brand ?? undefined); set("product", x.product ?? undefined); set("size", x.size ?? undefined);
    set("make", x.make ?? undefined); set("model", x.model ?? undefined); set("serial", x.serial ?? undefined); set("capacity", x.capacity ?? undefined); set("voltage", x.voltage ?? undefined);
    if (x.scanId && [...room.scanIds, ...(room.labelScanIds ?? [])].includes(x.scanId)) it.scanId = x.scanId;
    // R1, N3, L3: the reader's name when renamed, the counter it sits on, and a close-up: only one of this room's own photos.
    set("readAs", x.readAs ?? undefined); set("onCounter", x.onCounter ?? undefined);
    if (x.photoScanId && [...room.scanIds, ...(room.labelScanIds ?? [])].includes(x.photoScanId)) it.photoScanId = x.photoScanId;
    if (t.supplyKey) { it.supplyKey = t.supplyKey; it.par = x.par ?? suggestPar(x.count, o.people); }
    // Something not on our list (walkobjects.ts O1, O4): core maps its name to the nearest guide; the client never picks it.
    if (t.key === OTHER_TYPE) { const g = objectGuideFor(it.label); if (g) { it.guideId = g.id; it.guideName = g.name; } }
    return it;
  });
  // Walked again by someone else: their list is kept (nothing anyone confirmed is overwritten).
  const prior = room.acknowledged && room.acknowledged.by !== o.by ? [...(room.priorAcks ?? []), room.acknowledged] : room.priorAcks;
  const { toCheck: _toCheck, ...keep } = room;
  const nextRoomRow: WalkRoom = { ...keep, status: "done", acknowledged: { at: o.at, by: o.by, byName: o.byName, items, ...(o.notHere?.length ? { notHere: [...o.notHere] } : {}) }, ...(prior ? { priorAcks: prior } : {}) };
  const rooms = o.walk.rooms.map((r) => (r.key === room.key ? nextRoomRow : r));

  // The room's spot, with each "where" as a section of it.
  const spots = [...(o.location.storageSpots ?? [])].map((s) => ({ ...s, ...(s.sections ? { sections: [...s.sections] } : {}) }));
  let spotChanged = false;
  let spot = spots.find((s) => s.id === room.spotId);
  if (!spot) { spot = { id: room.spotId, name: room.name.slice(0, 60), holds: "any" }; spots.push(spot); spotChanged = true; }
  for (const it of items) if (it.supplyKey && it.where) {
    const secs = spot.sections ?? [];
    if (it.where.length <= SECTION_MAX && !secs.includes(it.where) && secs.length < SECTIONS_MAX) { spot.sections = [...secs, it.where]; spotChanged = true; }
  }

  // Supplies: one row per type; on-hand is the sum across every acknowledged room (W6).
  const before = JSON.stringify(o.location.supplies ?? []);
  const supplies: Supply[] = (o.location.supplies ?? []).map((s) => ({ ...s }));
  const all = rooms.flatMap((r) => (r.acknowledged?.items ?? []).map((it) => ({ it, r })));
  for (const it of items) {
    if (!it.supplyKey) continue;
    const seed = SUPPLY_SEEDS.find((s) => s.key === it.supplyKey);
    let cur = supplies.find((s) => s.key === it.supplyKey);
    if (!cur) { cur = { ...(seed ?? { key: it.supplyKey, name: it.label, kind: "cleaning" as SupplyKind }) }; supplies.push(cur); }
    if (!cur.spotId) { cur.spotId = spot.id; cur.whereKept = spot.name; if (it.where && spot.sections?.includes(it.where)) cur.section = it.where; }
    if (it.brand) cur.brand = it.brand;
    if (it.product) cur.product = it.product;
    if (it.size) cur.size = it.size;
    if (it.unit) cur.unit = it.unit;
    const counted = all.filter((x) => x.it.supplyKey === it.supplyKey && typeof x.it.count === "number");
    if (counted.length) cur.onHand = counted.reduce((n, x) => n + (x.it.count ?? 0), 0);
    if (cur.par === undefined && it.par !== undefined) cur.par = it.par;
  }

  // Equipment, floors, tables and seats → Things (a thing already there is updated, never doubled).
  const things = thingPlansFor(items, room, o.things);
  return {
    room: nextRoomRow,
    ...(JSON.stringify(supplies) !== before ? { supplies } : {}),
    ...(spotChanged ? { spots } : {}),
    things, items,
  };
}

/** Every thing an item made (thingId first, then thingIds), each once. */
export const thingIdsOf = (i: Pick<AckItem, "thingId" | "thingIds">): string[] => [...new Set([i.thingId, ...(i.thingIds ?? [])].filter((x): x is string => !!x))];
/** The item with one more thing it made: the first is thingId, every one is in thingIds. */
export const withThing = <T extends Pick<AckItem, "thingId" | "thingIds">>(i: T, id: string): T => { const ids = [...new Set([...thingIdsOf(i), id])]; return { ...i, thingId: ids[0], ...(ids.length > 1 ? { thingIds: ids } : {}) }; };

/**
 * The Things an acknowledged list makes (walkkinds.ts K4): one per item and guide, and one per unit for a cold or hot unit
 * ("Under-counter fridge 1", "Under-counter fridge 2": each its own daily check). A thing already in the room with the same name
 * and guide is updated, never doubled; else one of the same guide whose name isn't another walk kind's (an old walk's thing).
 */
export function thingPlansFor(items: readonly AckItem[], room: Pick<WalkRoom, "name" | "kind">, existing: readonly { id: string; typeId: string; room: string; name?: string; removed?: boolean }[], used: ReadonlySet<string> = new Set()): ThingPlan[] {
  const want: { typeId: string; name: string; it: AckItem }[] = [];
  for (const it of items) {
    const types = thingTypesFor(it, room.kind);
    const unit = isColdUnit(it) || isHotUnit(it);
    const n = unit ? Math.max(1, Math.min(UNITS_PER_ROW_MAX, it.count ?? 1)) : 1;
    for (const typeId of types) for (let k = 1; k <= n; k++) want.push({ typeId, name: n > 1 ? `${it.label} ${k}` : types.length > 1 && typeId === "surface:floor-drains" ? "Floor drain" : it.label, it });
  }
  const taken = new Set(used);
  const here = existing.filter((x) => !x.removed && x.room === room.name);
  const pick = new Map<number, string>();
  want.forEach((w, i) => { const x = here.find((h) => !taken.has(h.id) && h.typeId === w.typeId && h.name === w.name); if (x) { taken.add(x.id); pick.set(i, x.id); } });
  const kindNames = new Set(WALK_ITEMS.map((t) => t.label));
  want.forEach((w, i) => { if (pick.has(i)) return; const x = here.find((h) => !taken.has(h.id) && h.typeId === w.typeId && !(h.name && h.name !== w.it.label && kindNames.has(h.name.replace(/ \d+$/, "")))); if (x) { taken.add(x.id); pick.set(i, x.id); } });
  return want.map((w, i) => {
    const plan: ThingPlan = { typeId: w.typeId, name: w.name, room: room.name, ackId: w.it.id, ...(pick.has(i) ? { existingId: pick.get(i)! } : {}) };
    for (const k of ["make", "model", "serial", "capacity", "voltage"] as const) if (w.it[k]) plan[k] = w.it[k];
    return plan;
  });
}

/* ─────────────── the rule at this place (W3) ─────────────── */

/** A Food Code citation at this place: the state's own section (codeAt), or the Food Code with the pack's adoption line; plus how the state's text differs. */
export function walkRuleAt(fdaCite: string, place: PlaceRef): { cite: string; differs: string } {
  const pack = packFor(stateOrDefault(place.state));
  if (citesFoodCodeItself(place)) return { cite: `${fdaCite}${pack.foodCode.adoptsByReference ? `, adopted by ${pack.foodCode.adoptsByReference}` : ""}`, differs: "" };
  const c = codeAt(fdaCite, place);
  return { cite: c.line, differs: differsLine(c) };
}
/** The handwash sign's rule: the place's own mapped section when there is one (a county authority's code), else the pack's posting, else the Food Code at this place. */
function signRule(place: PlaceRef): { cite: string; differs: string } {
  const p = packFor(stateOrDefault(place.state)).postings.find((x) => x.id === "handwashing-sign");
  if (!citesFoodCodeItself(place) && codeAt("Food Code 6-301.14", place).own.length) return walkRuleAt("Food Code 6-301.14", place);
  return p ? { cite: p.source.citation, differs: "" } : walkRuleAt("Food Code 6-301.14", place);
}

/* ─────────────── the fix library (Easy Wins) ─────────────── */

export type FixAction =
  | { kind: "order"; supply: { key: string; name: string; kind: SupplyKind } }
  | { kind: "task"; title: string; how: string; schedule: { perShift?: boolean; everyDays?: number }; category: "opening" | "closing" | "cleaning" | "pest" | "maintenance" }
  | { kind: "pest_service"; title: string };

export interface WalkFix {
  id: string;
  /** What an inspector may look at, plain (a heading). */
  topic: string;
  /** The fix, one line. */
  fix: string;
  why: string;
  /** Rough time and cost ("10 minutes, about $40"). Our estimate, never a quote. */
  effort: string;
  /** FDA Food Code sections (verified in docs/source/research/food-code-2022-excerpts.txt and task-guides); cited at the place through ruleAt. */
  cite: string;
  action: FixAction;
  /** Other one-tap actions for the same fix. */
  more?: FixAction[];
}

const order = (key: string, name: string, kind: SupplyKind = "equipment"): FixAction => ({ kind: "order", supply: { key, name, kind } });

const FLOOR_WHY = "Where food is made, dishes are washed, and in restrooms and walk-ins, the floor doesn't soak up water.";
const FLOOR_EFFORT = "A flooring job; a flooring company can give you a quote";
/** Sourced fixes (W3). Citations are FDA Food Code 2022 sections; ruleAt turns each into the place's own code. */
export const WALK_FIXES: readonly WalkFix[] = [
  { id: "flies", topic: "Flies near the back door", fix: "Keep the back door closed, or add a door sweep or an air curtain.", why: "Outside openings are kept closed or screened so flies stay out.", effort: "10 minutes, about $25 for a door sweep", cite: "Food Code 6-202.15, 6-501.111", action: order("door-sweep", "Door sweep"), more: [{ kind: "task", title: "Keep the back door closed during service", how: "Close the back door after every delivery and trash run.", schedule: { perShift: true }, category: "opening" }, { kind: "pest_service", title: "Ask your pest control company about flies" }] },
  { id: "fly-light", topic: "Where the fly light is", fix: "Put the fly light away from food prep and food storage.", why: "Insects it catches can fall on food below it.", effort: "15 minutes, about $60 for a light trap", cite: "Food Code 6-202.13", action: order("insect-light-trap", "Insect light trap"), more: [{ kind: "task", title: "Check the fly lights", how: "Empty the tray and check the bulb.", schedule: { everyDays: 7 }, category: "pest" }] },
  { id: "pests", topic: "Signs of pests", fix: "Log a pest check, and ask your pest control company for a visit.", why: "The person in charge keeps pests out and checks often.", effort: "5 minutes to log it", cite: "Food Code 6-501.111", action: { kind: "pest_service", title: "Ask your pest control company for a visit" }, more: [{ kind: "task", title: "Do a pest check", how: "Look for droppings, flies and gnaw marks at opening.", schedule: { perShift: true }, category: "pest" }] },
  { id: "trash-lids", topic: "Trash cans", fix: "Use trash cans with lids, and empty them often.", why: "Covered trash draws fewer flies.", effort: "5 minutes, about $40 for a lidded can", cite: "Food Code 6-501.111", action: order("trash-can-lid", "Trash can with a lid") },
  { id: "handwash-sign", topic: "Handwash sign", fix: "Add a handwash sign at this sink.", why: "Workers see the reminder where they wash.", effort: "1 minute, free to print", cite: "Food Code 6-301.14", action: order("handwash-sign", "Handwash sign", "paper") },
  { id: "hand-soap", topic: "Soap at the hand sink", fix: "Keep hand soap at this sink.", why: "Every hand sink has soap.", effort: "2 minutes, about $10", cite: "Food Code 6-301.11", action: order("hand-soap", "Hand soap", "paper") },
  { id: "hand-towels", topic: "Towels at the hand sink", fix: "Keep paper towels at this sink.", why: "Every hand sink has a way to dry hands.", effort: "2 minutes, about $30 a case", cite: "Food Code 6-301.12", action: order("paper-towels", "Paper towels", "paper") },
  { id: "toilet-paper", topic: "Toilet paper", fix: "Keep a spare roll at each toilet.", why: "Each toilet has a supply of toilet paper.", effort: "1 minute", cite: "Food Code 6-302.11", action: order("toilet-paper", "Toilet paper", "paper") },
  { id: "test-strips", topic: "Sanitizer test strips", fix: "Keep test strips that match your sanitizer by the sink.", why: "The strength of the sanitizer is tested, and strips do that.", effort: "2 minutes, about $10", cite: "Food Code 4-302.14", action: order("test-strips", "Sanitizer test strips", "chemical") },
  { id: "cooler-thermometer", topic: "A thermometer in each cooler", fix: "Put a thermometer inside each cooler, where it's easy to read.", why: "Each cooler shows its temperature at a glance.", effort: "5 minutes, about $8 each", cite: "Food Code 4-204.112", action: order("cooler-thermometer", "Cooler thermometer") },
  { id: "food-thermometer", topic: "A food thermometer", fix: "Keep a probe thermometer where food is cooked and held.", why: "Food temperatures are checked with a thermometer.", effort: "2 minutes, about $20", cite: "Food Code 4-302.12", action: order("thermometer", "Probe thermometer") },
  { id: "wiping-cloths", topic: "Wiping cloths", fix: "Keep wet wiping cloths in a sanitizer bucket between uses.", why: "A cloth that sits in sanitizer stays clean.", effort: "5 minutes, about $15 for a bucket", cite: "Food Code 3-304.14", action: order("sanitizer-bucket", "Sanitizer bucket", "cleaning"), more: [{ kind: "task", title: "Change the cloth bucket", how: "Fresh sanitizer, then test it with a strip.", schedule: { perShift: true }, category: "opening" }] },
  // Floors (walkkinds.ts K2): 6-101.11(A)(3) nonabsorbent where food is made, dishes are washed, in walk-ins and toilet rooms;
  // 6-201.14(A) no carpet there. A flooring job, so the action is a reminder to get a quote; never a number we can't source.
  { id: "floor-carpet", topic: "Carpet in this room", fix: "Plan a smooth floor here that doesn't soak up water, like sealed tile, sealed concrete, vinyl or epoxy.", why: FLOOR_WHY + " Carpet is fine in rooms like the dining room.", effort: FLOOR_EFFORT, cite: "Food Code 6-101.11, 6-201.14", action: { kind: "task", title: "Get a quote for a smooth floor", how: "Ask a flooring company for a quote.", schedule: { everyDays: 90 }, category: "maintenance" } },
  { id: "floor-seal", topic: "A wood floor that isn't sealed", fix: "Seal the wood floor here so water can't soak in.", why: FLOOR_WHY, effort: FLOOR_EFFORT, cite: "Food Code 6-101.11", action: { kind: "task", title: "Get a quote to seal the wood floor", how: "Ask a flooring company for a quote.", schedule: { everyDays: 90 }, category: "maintenance" } },
  { id: "cold-holding", topic: "Cold food in the case", fix: "Check the case with a thermometer each morning.", why: "Cold food stays at 41°F or below.", effort: "1 minute a day", cite: "Food Code 3-501.16", action: { kind: "task", title: "Check the cold case temperature", how: "Read the thermometer at opening and write it down.", schedule: { perShift: true }, category: "opening" } },
];
export const walkFix = (id: string | undefined | null): WalkFix | undefined => (id ? WALK_FIXES.find((f) => f.id === id) : undefined);
export const WALK_FIX_IDS: readonly string[] = WALK_FIXES.map((f) => f.id);

/* ─────────────── what the inspector looks for here (W3, W4) ─────────────── */

export type LookStatus = "have" | "to_add" | "check";
export interface LookItem { key: string; what: string; status: LookStatus; /** "You have: …" or the fix. */ line: string; cite: string; differs?: string; fixId?: string }

const hasType = (items: readonly AckItem[], ...keys: string[]) => items.some((i) => keys.includes(i.typeKey));
const countOf = (items: readonly AckItem[], key: string) => items.filter((i) => i.typeKey === key).reduce((n, i) => n + Math.max(1, i.count ?? 1), 0);

/**
 * For one room: what an inspector looks for, compared with what the walk found (acknowledged items, else the suggestions the
 * person is reviewing). Ordered: what's already there first, then "To add". Never alarming: "To add", with the fix.
 */
export function lookFor(kind: WalkRoomKind, items: readonly AckItem[], place: PlaceRef): LookItem[] {
  const out: LookItem[] = [];
  const handSinkRoom = ["restroom", "kitchen", "dish", "bar"].includes(kind) || hasType(items, "hand-sink");
  if (handSinkRoom) {
    const soap = walkRuleAt("Food Code 6-301.11", place);
    out.push({ key: "hand-soap", what: "Soap at the hand sink", status: hasType(items, "hand-soap") ? "have" : "to_add", line: hasType(items, "hand-soap") ? "Hand soap at the hand sink." : walkFix("hand-soap")!.fix, cite: soap.cite, fixId: "hand-soap" });
    const dry = walkRuleAt("Food Code 6-301.12", place);
    const towels = hasType(items, "paper-towels"), dryer = hasType(items, "hand-dryer");
    const st: LookStatus = towels ? "have" : dryer ? (dry.differs ? "check" : "have") : "to_add";
    out.push({ key: "hand-towels", what: "Towels or a dryer at the hand sink", status: st, line: towels ? "Paper towels at the hand sink." : dryer ? (dry.differs ? "A hand dryer here. Check it's allowed in this room." : "A hand dryer at the hand sink.") : walkFix("hand-towels")!.fix, cite: dry.cite, ...(dry.differs ? { differs: dry.differs } : {}), fixId: "hand-towels" });
    const sign = signRule(place);
    out.push({ key: "handwash-sign", what: "Handwash sign", status: hasType(items, "handwash-sign") ? "have" : "to_add", line: hasType(items, "handwash-sign") ? "A handwash sign at the sink." : walkFix("handwash-sign")!.fix, cite: sign.cite, fixId: "handwash-sign" });
  }
  if (kind === "restroom") {
    const r = walkRuleAt("Food Code 6-302.11", place);
    out.push({ key: "toilet-paper", what: "Toilet paper", status: hasType(items, "toilet-paper") ? "have" : "to_add", line: hasType(items, "toilet-paper") ? "Toilet paper at the toilet." : walkFix("toilet-paper")!.fix, cite: r.cite, fixId: "toilet-paper" });
  }
  if (kind === "dish" || hasType(items, "three-compartment-sink", "dish-machine", "sanitizer")) {
    const r = walkRuleAt("Food Code 4-302.14", place);
    out.push({ key: "test-strips", what: "Sanitizer test strips", status: hasType(items, "test-strips") ? "have" : "to_add", line: hasType(items, "test-strips") ? "Test strips by the sink." : walkFix("test-strips")!.fix, cite: r.cite, fixId: "test-strips" });
  }
  // The floor (walkkinds.ts K2): what it is, and an Easy Win only where it must not soak up water.
  const floor = items.find((i) => walkItem(i.typeKey)?.group === "floor" && WALK_FLOORS.some((f) => f.key === i.material));
  if (floor) {
    const fix = floorFixId(floor.material, kind);
    const r = walkRuleAt(fix === "floor-seal" ? "Food Code 6-101.11" : "Food Code 6-101.11, 6-201.14", place);
    const f = WALK_FLOORS.find((x) => x.key === floor.material)!;
    const have = `${f.label} floor${floor.drain ? ", with a floor drain" : ""}.`;
    out.push({ key: "floor", what: fix ? walkFix(fix)!.topic : "The floor", status: fix ? "to_add" : "have", line: fix ? walkFix(fix)!.fix : have, cite: r.cite, ...(r.differs ? { differs: r.differs } : {}), ...(fix ? { fixId: fix } : {}) });
  }
  const coolers = items.filter((i) => isColdUnit(i)).reduce((n, i) => n + Math.max(1, i.count ?? 1), 0);
  if (coolers > 0 || kind === "walkin") {
    const r = walkRuleAt("Food Code 4-204.112", place);
    const need = Math.max(coolers, 1), have = countOf(items, "cooler-thermometer");
    out.push({ key: "cooler-thermometer", what: "A thermometer in each cooler", status: have >= need ? "have" : "to_add", line: have >= need ? `${have === 1 ? "A thermometer" : `${have} thermometers`} in the cooler${need === 1 ? "" : "s"}.` : need > 1 ? `Put a thermometer inside each cooler (${have} of ${need} have one).` : walkFix("cooler-thermometer")!.fix, cite: r.cite, fixId: "cooler-thermometer" });
  }
  if (kind === "kitchen") {
    const r = walkRuleAt("Food Code 4-302.12", place);
    out.push({ key: "food-thermometer", what: "A food thermometer", status: hasType(items, "food-thermometer") ? "have" : "to_add", line: hasType(items, "food-thermometer") ? "A food thermometer on the line." : walkFix("food-thermometer")!.fix, cite: r.cite, fixId: "food-thermometer" });
  }
  if (hasType(items, "wiping-cloths")) {
    const r = walkRuleAt("Food Code 3-304.14", place);
    out.push({ key: "wiping-cloths", what: "Wiping cloths kept in sanitizer", status: hasType(items, "sanitizer-bucket") ? "have" : "to_add", line: hasType(items, "sanitizer-bucket") ? "Cloths in a sanitizer bucket." : walkFix("wiping-cloths")!.fix, cite: r.cite, fixId: "wiping-cloths" });
  }
  const rank: Record<LookStatus, number> = { have: 0, check: 1, to_add: 2 };
  return out.sort((a, b) => rank[a.status] - rank[b.status]);
}

/* ─────────────── Easy Wins ─────────────── */

export const EASY_WINS_TITLE = "Easy Wins";
/** Words we never write for the owner (W4). The department's own words, quoted, are theirs. */
export const JUDGEMENT_WORDS = /\b(violations?|violated|fail(s|ed|ing|ure)?|bad|unsafe|dirty|wrong|poor|gross|filthy|negligent|unacceptable)\b/i;
export const judgementWordsIn = (s: string): string[] => [...s.matchAll(new RegExp(JUDGEMENT_WORDS.source, "gi"))].map((m) => m[0]);

export interface WalkWin {
  key: string;
  from: "walk" | "spotted" | "inspection";
  /** The room it's in (walk, spotted). */
  room?: string;
  topic: string;
  fix: string;
  why: string;
  effort: string;
  cite: string;
  differs?: string;
  actions: FixAction[];
  /** Inspection-derived: what the department wrote, in its words, its date and the link to its record. */
  noted?: { text: string; date: string; dateText: string; sourceUrl: string; department: string };
}

/** Inspection-derived item → one Easy Win: "Your last inspection noted …" with the check that answers it. */
function inspectionWin(t: AheadThing, src: { department: string; url: string }): WalkWin | undefined {
  if (!t.cited) return undefined;
  const d = AHEAD[t.item];
  if (!d) return undefined;
  return {
    key: `inspection:${t.item}`, from: "inspection", topic: d.thing, fix: d.title, why: d.help, effort: `${d.minutes} minute${d.minutes === 1 ? "" : "s"}`,
    cite: `${src.department}, item ${t.cited.code.trim()}`,
    actions: [{ kind: "task", title: d.title, how: d.help, schedule: { perShift: true }, category: "opening" }],
    noted: { text: t.cited.text || `Item ${t.cited.code}`, date: t.cited.date, dateText: t.cited.dateText, sourceUrl: src.url, department: src.department },
  };
}

/**
 * Easy Wins for the hand-off: the walk's "To add" items (each room), confirmed spotted issues (W5: never a suggestion), then the
 * items their own inspections noted (only when the record is shown now; a held source passes none). Hidden ones are left out.
 */
export function walkEasyWins(walk: Pick<RoomWalk, "rooms">, place: PlaceRef, o: { inspection?: { things: readonly AheadThing[]; source: { department: string; url: string } } | null } = {}): WalkWin[] {
  const out: WalkWin[] = [];
  const seen = new Set<string>();
  for (const r of walk.rooms) {
    const hidden = new Set(r.hidden ?? []);
    if (r.acknowledged) for (const li of lookFor(r.kind, r.acknowledged.items, place)) {
      if (li.status === "have" || !li.fixId) continue;
      const f = walkFix(li.fixId)!;
      const key = `walk:${r.key}:${li.key}`;
      if (hidden.has(key)) continue;
      out.push({ key, from: "walk", room: r.name, topic: li.what, fix: li.status === "check" ? li.line : `${f.fix.replace(/ at this sink\.$/, ` in the ${r.name.toLowerCase()}.`)}`, why: f.why, effort: f.effort, cite: li.cite, ...(li.differs ? { differs: li.differs } : {}), actions: [f.action, ...(f.more ?? [])] });
    }
    for (const is of r.issues) {
      if (is.status !== "confirmed") continue;
      const f = walkFix(is.fixId);
      const key = `spotted:${r.key}:${is.id}`;
      if (!f || hidden.has(key) || seen.has(`${r.key}:${f.id}`)) continue;
      seen.add(`${r.key}:${f.id}`);
      const rule = walkRuleAt(f.cite, place);
      out.push({ key, from: "spotted", room: r.name, topic: f.topic, fix: f.fix, why: f.why, effort: f.effort, cite: rule.cite, ...(rule.differs ? { differs: rule.differs } : {}), actions: [f.action, ...(f.more ?? [])] });
    }
  }
  for (const t of o.inspection?.things ?? []) {
    if (t.from !== "flagged") continue;
    const w = inspectionWin(t, o.inspection!.source);
    if (w && !walk.rooms.some((r) => r.hidden?.includes(w.key))) out.push(w);
  }
  return out;
}

/* ─────────────── hand it off ─────────────── */

export interface HandoffSummary {
  line: string;
  rooms: number;
  supplies: number;
  equipment: number;
  toAdd: number;
  done: string[];
  toWalk: { key: string; name: string }[];
  setBy: string;
  setOn: string;
  next: { kind: "take_over" | "walk" | "first_check"; label: string; to: string; line: string };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/**
 * The summary the owner sees when they take over. Counts come from what was acknowledged only (a test checks they match).
 * `viewerIsSetter`: the person looking walked it; `setByOther`: someone else (staff on a setup visit) walked it and the viewer
 * hasn't taken it over yet.
 */
export function handoffSummary(walk: RoomWalk, o: { setOn: string; viewerId: Id; place: PlaceRef; easyWinCount?: number }): HandoffSummary {
  const acked = walk.rooms.filter((r) => r.status === "done" && r.acknowledged);
  const items = acked.flatMap((r) => r.acknowledged!.items);
  const supplies = new Set(items.filter((i) => i.kind === "supply").map((i) => i.supplyKey ?? i.typeKey)).size;
  const equipment = items.filter((i) => i.kind === "equipment").reduce((n, i) => n + Math.max(1, i.count ?? 1), 0);
  const toAdd = o.easyWinCount ?? acked.reduce((n, r) => n + lookFor(r.kind, r.acknowledged!.items, o.place).filter((x) => x.status !== "have").length, 0);
  const toWalk = walk.rooms.filter((r) => r.status !== "done").map((r) => ({ key: r.key, name: r.name }));
  const setBy = walk.prep?.staffName ?? walk.startedByName;
  const takeOver = !!walk.prep && !walk.handedOffAt && o.viewerId !== walk.startedBy;
  const next: HandoffSummary["next"] = takeOver
    ? { kind: "take_over", label: "Make it yours", to: "/walk", line: `${setBy} set this up for you. Make it yours to keep it.` }
    : toWalk.length ? { kind: "walk", label: `Walk the ${toWalk[0]!.name.toLowerCase()} now`, to: `/walk?room=${encodeURIComponent(toWalk[0]!.key)}`, line: `To walk: ${toWalk.map((r) => r.name).join(", ")}.` }
    : { kind: "first_check", label: "Start your first check", to: "/do", line: "Everything's walked. Your first check is ready." };
  const line = `Your place is set up: ${plural(acked.length, "room")}, ${plural(supplies, "supply", "supplies")}, ${plural(equipment, "piece", "pieces")} of equipment, ${plural(toAdd, "thing")} to add. Set up by ${setBy} on ${o.setOn}.`;
  return { line, rooms: acked.length, supplies, equipment, toAdd, done: acked.map((r) => r.name), toWalk, setBy, setOn: o.setOn, next };
}

/** "Done: Restroom, Dish area · To walk: Kitchen" */
export function progressLine(walk: Pick<RoomWalk, "rooms">): string {
  const done = walk.rooms.filter((r) => r.status === "done").map((r) => r.name);
  const rest = walk.rooms.filter((r) => r.status !== "done").map((r) => r.name);
  return [done.length ? `Done: ${done.join(", ")}` : "", rest.length ? `To walk: ${rest.join(", ")}` : ""].filter(Boolean).join(" · ") || "No rooms yet.";
}

/* ─────────────── the map ─────────────── */

export interface MapRoom { key: string; name: string; kind: WalkRoomKind; status: WalkRoomStatus; statusWord: string; photoScanIds: string[]; spot: { id: string; name: string; sections: string[] }; items: { id: string; label: string; line: string; where?: string; /** The name part of the line (previewtouch.ts itemName). */ name?: string; /** The owner renamed it on the preview link. */ renamed?: true }[] }

/** One plain line for an item: "Purell hand soap · 1 L · 2", "Espresso machine · La Marzocco Linea · ••••4821". */
export function itemLine(i: AckItem): string {
  const t = walkItem(i.typeKey);
  const name = (i.ownerNamedFrom ? i.label : "") || [i.brand, i.product].filter(Boolean).join(" ") || i.label || t?.label || i.typeKey;
  const parts = [name];
  const m = materialOf(t?.group, i.material);
  if (m) parts.push(m.label);
  if (t?.group === "floor" && i.drain) parts.push("floor drain");
  if (t?.kind === "equipment") { const mm = [i.make, i.model].filter(Boolean).join(" "); if (mm) parts.push(mm); const s = maskSerial(i.serial); if (s) parts.push(s); if (i.capacity) parts.push(i.capacity); if (i.voltage) parts.push(i.voltage); const h = t.group === "case" ? holdingOf(i) : i.holding; if (h && h !== "none") parts.push(h === "hot" ? "hot holding" : "cold holding"); }
  else if (i.size) parts.push(i.size);
  if (typeof i.count === "number" && i.count > 1) parts.push(`${i.count}${i.unit ? ` ${i.unit}` : ""}`);
  return parts.join(" · ");
}

/** The place as blocks in the walk's order: each room, its spot and sections, its photos and what's kept there. */
export function placeMap(walk: Pick<RoomWalk, "rooms">): MapRoom[] {
  return walk.rooms.map((r) => ({
    key: r.key, name: r.name, kind: r.kind, status: r.status, statusWord: WALK_STATUS_WORD[r.status], photoScanIds: [...r.scanIds],
    spot: { id: r.spotId, name: r.name, sections: [...new Set((r.acknowledged?.items ?? []).map((i) => i.where).filter((w): w is string => !!w))] },
    items: (r.acknowledged?.items ?? []).map((i) => ({ id: i.id, label: i.label, line: itemLine(i), name: (i.ownerNamedFrom ? i.label : "") || [i.brand, i.product].filter(Boolean).join(" ") || i.label || walkItem(i.typeKey)?.label || i.typeKey, ...(i.where ? { where: i.where } : {}), ...(i.ownerNamedFrom ? { renamed: true as const } : {}) })),
  }));
}

/**
 * One room's kinds, grouped, for the binder's "Your place" page (walkkinds.ts):
 * "Floor: sealed concrete, floor drain · Tables: 6 two-tops (laminate), 2 high-tops (wood) · Seats: 12 chairs (metal) ·
 * Cases: 1 glass pastry case (cold) · Fridges: 2 under-counter, 1 reach-in". Empty when the room has none of these.
 */
export function roomKindsLine(items: readonly AckItem[]): string {
  const parts: string[] = [];
  for (const group of WALK_GROUPS) {
    const g = { group };
    const its = items.filter((i) => walkItem(i.typeKey)?.group === g.group);
    if (!its.length) continue;
    if (g.group === "floor") {
      const f = its[0]!;
      const m = materialOf("floor", f.material);
      const words = [m?.short ?? "", f.drain ? "floor drain" : f.drain === false ? "no floor drain" : ""].filter(Boolean);
      if (words.length) parts.push(`Floor: ${words.join(", ")}`);
      continue;
    }
    const each = its.map((i) => {
      const t = walkItem(i.typeKey)!;
      const n = Math.max(1, i.count ?? 1);
      const [one, many] = t.short ?? [t.label.toLowerCase(), t.label.toLowerCase()];
      const extra = g.group === "case" ? holdingOf(i) : materialOf(g.group, i.material)?.short;
      return `${n} ${n === 1 ? one : many}${extra && extra !== "none" ? ` (${extra})` : ""}`;
    });
    parts.push(`${GROUP_TITLE[g.group]}: ${each.join(", ")}`);
  }
  return parts.join(" · ");
}

/** The printable one-page "Your place" sheet for the binder: rooms, equipment make and model, supplies. */
export function placeSheet(walk: RoomWalk, o: { placeName: string; setOn: string }): { title: string; subtitle: string; rooms: { name: string; kinds?: string; equipment: string[]; supplies: string[]; other: string[] }[] } {
  return {
    title: `Your place: ${o.placeName}`,
    subtitle: `Walked by ${walk.prep?.staffName ?? walk.startedByName} on ${o.setOn}.${walkChangedBy(walk) ? ` Changed by ${walkChangedBy(walk)}.` : ""}`,
    rooms: walk.rooms.filter((r) => r.acknowledged).map((r) => {
      const its = r.acknowledged!.items;
      const lineOf = (i: AckItem) => `${itemLine(i)}${i.where ? ` (${i.where})` : ""}`;
      const kinds = roomKindsLine(its);
      return { name: r.name, ...(kinds ? { kinds } : {}), equipment: its.filter((i) => i.kind === "equipment").map((i) => `${lineOf(i)}${i.kind === "equipment" && !i.make && !i.model ? ` · ${MODEL_NOT_READABLE}` : ""}`), supplies: its.filter((i) => i.kind === "supply").map((i) => `${lineOf(i)}${!i.brand ? ` · ${BRAND_NOT_READABLE}` : ""}`), other: its.filter((i) => i.kind === "sign" || i.kind === "furniture").map(lineOf) };
    }),
  };
}

/** The binder's "What's in place" section: the walk is the place's first record, dated and attributed. */
export function walkBinderSection(walk: RoomWalk, timeLabel: (iso: string) => string): { id: string; heading: string; intro: string; empty: string; table?: { columns: string[]; widths: number[]; rows: { cells: string[] }[] } } {
  const by = (r: WalkRoom, i: AckItem) => i.addedBy ? `Added by ${i.addedBy.byName}` : i.changes?.length ? `${r.acknowledged!.byName} · changed by ${i.changes[i.changes.length - 1]!.byName}` : r.acknowledged!.byName;
  const when = (r: WalkRoom, i: AckItem) => timeLabel(i.addedBy?.at ?? i.changes?.[i.changes.length - 1]?.at ?? r.acknowledged!.at);
  // Each room's kinds first, grouped (roomKindsLine), then one row per item.
  const rows = walk.rooms.filter((r) => r.acknowledged).flatMap((r) => {
    const kinds = roomKindsLine(r.acknowledged!.items);
    return [...(kinds ? [{ cells: [timeLabel(r.acknowledged!.at), r.name, kinds, r.acknowledged!.byName] }] : []), ...r.acknowledged!.items.map((i) => ({ cells: [when(r, i), r.name, `${itemLine(i)}${i.where ? ` (${i.where})` : ""}`, by(r, i)] }))];
  });
  const changer = walkChangedBy(walk);
  return {
    id: "walk", heading: "What's in place (room walk)",
    intro: `The rooms were walked and each item confirmed by a person. Walked by ${walk.prep?.staffName ?? walk.startedByName}${changer ? ` · changed by ${changer}` : ""}. Photos are on file.`,
    empty: "No rooms confirmed yet.",
    ...(rows.length ? { table: { columns: ["When", "Room", "What's there", "Confirmed by"], widths: [1.3, 1.3, 4, 1.4], rows } } : {}),
  };
}

/** The name of whoever changed what was set up after hand-off (the newest change), or null: "Set up by Zak · changed by Rosa". */
export function walkChangedBy(walk: Pick<RoomWalk, "rooms" | "removedRooms" | "handedOffAt">): string | null {
  const stamps: WalkStamp[] = [];
  for (const r of walk.rooms) {
    for (const i of r.acknowledged?.items ?? []) { if (i.addedBy) stamps.push(i.addedBy); for (const c of i.changes ?? []) stamps.push(c); }
    for (const i of r.removedItems ?? []) stamps.push(i.removed);
    for (const p of r.photoLog ?? []) stamps.push(p);
    if (r.addedBy) stamps.push(r.addedBy);
  }
  for (const r of walk.removedRooms ?? []) stamps.push(r.removed);
  return stamps.sort((a, b) => b.at.localeCompare(a.at))[0]?.byName ?? null;
}

/** Everything the owner-facing words say, for the judgement-word lint (W4). */
export function ownerWords(wins: readonly WalkWin[], looks: readonly LookItem[], summary?: HandoffSummary): string {
  return [...wins.flatMap((w) => [w.topic, w.fix, w.why, w.effort, ...w.actions.map((a) => (a.kind === "order" ? a.supply.name : a.title))]), ...looks.flatMap((l) => [l.what, l.line]), summary?.line ?? "", summary?.next.label ?? "", summary?.next.line ?? "", EASY_WINS_TITLE].join("\n");
}

/* ─────────────── what the screens get (API: routes/roomWalk.ts, routes/prep.ts) ─────────────── */

/** The items a room's photos suggest, for the review screen ("Purell hand soap · 1 L · at the handwash sink — Add"). */
export interface RoomSuggestion {
  id: string; scanId: string; typeKey: string; label: string; count?: number; where?: string; holding?: string; /** A kind's material or floor type, a suggestion like the rest (walkkinds.ts). */ material?: string; drain?: boolean; confidence: number; evidence: string; line: string;
  /** How sure the reader is of the count (0–1) and how it counted ("3 by the window, 3 along the wall"). Older reads have neither. */
  countConfidence?: number; countEvidence?: string;
  /** The counter this sits on, in the photo's words ("Back bar"): it becomes a child of that counter's row (walkcounters.ts). */
  sitsOn?: string;
  /** Something not on our list (walkobjects.ts): the guide its name maps to, or null (the general rule). Only on typeKey "other". */
  guide?: ObjectGuide | null;
}
export interface LabelReading { scanId: string; label: string; fields: Record<string, string>; status: SetupScan["status"] }
export interface IssueSuggestion { id: string; scanId: string; fixId: string; topic: string; fix: string; evidence: string; confidence: number; status: "suggested" | "confirmed" | "dismissed" }

/**
 * Burst photos (core walkmerge.ts): each photo of the room with how far it got, so its thumbnail can say so on its own.
 * reading: the reader has it · read: done (found: how many things it showed) · failed: couldn't read it (Try again) ·
 * capped: saved, but today's photo reading limit was reached (read it later) · duplicate: the same picture again (counted once).
 */
export interface WalkPhotoState { scanId: string; kind: "room" | "label"; status: "reading" | "read" | "failed" | "capped" | "duplicate"; found: number; error?: string; /** Clockwise turn the served picture carries (SetupScan.rotation); a change means the picture to show changed. */ rotation?: 90 | 180 | 270 }

/** The daily cap's words on a photo it didn't read (services/api scan/dailyCap.ts uses this exact line). */
export const SCAN_CAP_MESSAGE = "Today's scan limit is reached. Type it in, or try again tomorrow.";

export interface WalkRoomView extends WalkRoom {
  look: LookItem[];
  suggestions: RoomSuggestion[];
  /** One row per real thing across the room's photos (walkmerge.ts M1–M6). Older servers omit it. */
  rows?: WalkRow[];
  /** Each photo's own state (burst photos). Older servers omit it. */
  photos?: WalkPhotoState[];
  labels: LabelReading[];
  issueSuggestions: IssueSuggestion[];
  pending: number;
  failed: string[];
  lookPreview: LookItem[];
}

export interface WalkView {
  walk: RoomWalk;
  rooms: WalkRoomView[];
  map: MapRoom[];
  progress: string;
  summary: HandoffSummary;
  /** Owner and admin, and the person walking until it's handed off; null for anyone else (never staff). */
  easyWins: WalkWin[] | null;
  /** "Prepping Loki for the owner": shown to the staff member while prepping. */
  prepBanner: string | null;
  /** The preparer's own next step at hand-off (hand walk A7): review it and send it to the owner, where that lives. Null for anyone else. */
  prepNext?: { label: string; to: string; line: string } | null;
  canTakeOver: boolean;
  /** L6: every room's rows confirmed or marked not here (walkchecklist.ts); `line` says what is left. */
  completion: WalkCompletion;
  /** P1: "2 rooms done for you · 4 to go" and the next room, for a prep walk (else null). */
  prepProgress: PrepProgress | null;
  sheet: ReturnType<typeof placeSheet>;
  placeName: string;
  setOn: string;
  /** Make it yours (walkreview.ts): the owner's review of what staff set up; null when there is none (not prepped, not handed off). */
  review?: import("./walkreview.ts").WalkReviewView | null;
}

/** Exactly what the prospect sees (and the staff member reviews): photo ids only; each screen builds its own photo address. */
export interface PreviewData {
  business: string;
  placeName: string;
  setBy: string;
  setOn: string;
  /** "Set up for you by Maya, your manager, on Oct 9." (core prep.ts setByLine; P6). Older servers omit it. */
  setByLine?: string;
  /** Benefits first (core prep.ts prepBenefits): "1 room walked with 2 photos", "4 daily checks ready for your team"… */
  benefits?: string[];
  /** Who prepared it, for the claim screen: a customer preparer may be kept on as a manager (P7); staff never. */
  preparer?: { name: string; kind: "staff" | "customer"; askKeep: boolean };
  map: MapRoom[];
  inPlace: { room: string; lines: string[] }[];
  firstLog: ReturnType<typeof walkBinderSection>;
  easyWins: WalkWin[];
  fromInspections: WalkWin[];
  /** "Your last inspection (Jun 3, 2026) cited …" (lastinspection.ts handoffInspectionOf), near the top. Null: no linked record shown now. */
  lastInspection?: import("./lastinspection.ts").HandoffInspection | null;
  summary: HandoffSummary;
  writeUp: string;
  note: string;
  photos: string[];
  expiresAt?: string;
}

/* ─────────────── composing the view (one function for the API and both demos) ─────────────── */


export function roomSuggestions(scans: SetupScan[], room: WalkRoom): { items: RoomSuggestion[]; labels: LabelReading[]; issues: IssueSuggestion[]; pending: number; failed: string[]; photos: WalkPhotoState[] } {
  // Only the room's photos now: a retaken or removed photo kept on file adds nothing (security run-13 #2). A duplicate is never on the
  // room's list (the picture was already sent): it stays unless it was taken out.
  const live = new Set([...room.scanIds, ...(room.labelScanIds ?? [])]), out = new Set(room.removedScanIds ?? []);
  const all = scans.filter((s) => s.roomKey === room.key && (live.has(s.id) || (s.status === "duplicate" && !out.has(s.id))));
  // The same picture sent twice (a double tap; a resend after a lost answer) is one photo: its copy adds nothing (walkmerge.ts M4).
  const here = new Set(all.map((s) => s.id));
  const mine = all.filter((s) => !(s.status === "duplicate" && s.duplicateOf && here.has(s.duplicateOf)));
  // In the walk's own order (the order they were added), so a thumbnail never moves when a read finishes.
  const order = [...room.scanIds, ...(room.labelScanIds ?? [])];
  const rank = (id: string) => { const i = order.indexOf(id); return i < 0 ? order.length : i; };
  const photos: WalkPhotoState[] = all.filter((s) => s.kind === "room" || s.kind === "label").sort((a, b) => rank(a.id) - rank(b.id) || a.capturedAt.localeCompare(b.capturedAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map((s) => ({
    scanId: s.id, kind: s.kind as "room" | "label",
    status: s.status === "processing" ? "reading" : s.status === "duplicate" ? "duplicate" : s.status === "failed" ? (s.error === SCAN_CAP_MESSAGE ? "capped" : "failed") : "read",
    found: s.status === "duplicate" ? 0 : s.suggestions.filter((x) => x.kind === "walk_item" || x.kind === "walk_label").length,
    ...(s.status === "failed" ? { error: s.error ?? "This photo couldn't be read." } : {}),
    ...(s.rotation ? { rotation: s.rotation } : {}),
  }));
  const items: RoomSuggestion[] = [], labels: LabelReading[] = [], issues: IssueSuggestion[] = [];
  const decided = new Map(room.issues.map((i) => [i.id, i]));
  for (const s of mine) {
    if (s.kind === "label") { const l = s.suggestions.find((x) => x.kind === "walk_label"); labels.push({ scanId: s.id, label: l?.label ?? "Label", fields: Object.fromEntries(Object.entries(l?.fields ?? {}).filter(([, v]) => typeof v === "string")) as Record<string, string>, status: s.status }); continue; }
    for (const x of s.suggestions) {
      if (x.kind === "walk_item") {
        let t = walkItem(String(x.fields.itemType ?? ""));
        if (!t) continue;
        // Something not on our list (walkobjects.ts O1): a name that IS one of our kinds becomes that kind; else it keeps its own
        // name with the nearest guide, or the general rule (O2). Never dropped.
        let name: string | null = null, guide: ObjectGuide | null | undefined;
        if (t.key === OTHER_TYPE) {
          name = cleanObjectName(x.label);
          if (!name) continue;
          const k = walkTypeForName(name);
          if (k) { t = walkItem(k)!; name = null; } else guide = objectGuideFor(name);
        }
        const count = typeof x.fields.count === "number" ? x.fields.count : undefined;
        const where = typeof x.fields.where === "string" ? x.fields.where : undefined;
        // A material only from the kind's own choices; a drain only on a floor (walkkinds.ts K1).
        const mat = materialOf(t.group, typeof x.fields.material === "string" ? x.fields.material : null);
        const drain = t.group === "floor" && typeof x.fields.drain === "boolean" ? x.fields.drain : undefined;
        const holding = typeof x.fields.holding === "string" && HOLDING.includes(x.fields.holding as Holding) ? x.fields.holding : undefined;
        const cc = typeof x.fields.countConfidence === "number" && Number.isFinite(x.fields.countConfidence) ? Math.min(1, Math.max(0, x.fields.countConfidence)) : undefined;
        const ce = typeof x.fields.countEvidence === "string" && x.fields.countEvidence.trim() ? x.fields.countEvidence.trim().slice(0, 120) : undefined;
        // A counter is named by where it is or what it is for ("Front counter", "Back bar"); what sits on it names its counter (walkcounters.ts).
        const label = name ?? (t.group === "counter" ? counterName(x.label, where) : t.label);
        const sitsOn = t.group === "counter" ? "" : cleanObjectName(x.fields.onCounter);
        items.push({ id: `${s.id}:${x.id}`, scanId: s.id, typeKey: t.key, label, ...(sitsOn ? { sitsOn } : {}), ...(count !== undefined ? { count } : {}), ...(where ? { where } : {}), ...(holding ? { holding } : {}), ...(mat ? { material: mat.key } : {}), ...(drain !== undefined ? { drain } : {}), ...(cc !== undefined ? { countConfidence: cc } : {}), ...(ce ? { countEvidence: ce } : {}), ...(t.key === OTHER_TYPE ? { guide: guide ?? null } : {}), confidence: x.confidence, evidence: x.evidence, line: [label, mat?.label ?? "", drain ? "floor drain" : "", count && count > 1 ? `${count}` : "", where ?? ""].filter(Boolean).join(" · ") });
      } else if (x.kind === "walk_issue") {
        const f = walkFix(String(x.fields.fixId ?? ""));
        if (!f) continue;
        const id = `${s.id}:${x.id}`;
        issues.push({ id, scanId: s.id, fixId: f.id, topic: f.topic, fix: f.fix, evidence: x.evidence, confidence: x.confidence, status: decided.get(id)?.status ?? "suggested" });
      }
    }
  }
  return { items, labels, issues, pending: mine.filter((s) => s.status === "processing").length, failed: mine.filter((s) => s.status === "failed").map((s) => s.error ?? "This photo couldn't be read."), photos };
}


const dayText = (iso: string, tz: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: tz });

/** Who may see Easy Wins (they name the business's own issues): owner, admin, and the person walking until hand-off. */
export function seesWins(role: string | null, viewerId: Id | null, walk: RoomWalk): boolean {
  if (role === "owner" || role === "admin") return true;
  return !!viewerId && viewerId === walk.startedBy && !walk.handedOffAt;
}

export function composeWalkView(o: { walk: RoomWalk; scans: readonly SetupScan[]; orgName: string; placeName: string; timeZone: string; place: PlaceRef; viewerId: Id | null; role: string | null; prospect?: boolean; /** An ownership offer for this place waits for the viewer (core ownership.ts). */ offerForViewer?: boolean; inspection?: { things: readonly AheadThing[]; source: { department: string; url: string } } | null; /** Where the preparer reviews and sends it (the team site for staff, /prep/<org> in the app for a customer). */ prepReviewTo?: string | null; /** The business kind (a coffee shop's next room is the bar, a restaurant's the kitchen). */ orgKind?: string }): WalkView {
  const { walk, place } = o;
  const rooms: WalkRoomView[] = walk.rooms.map((r) => {
    const s = roomSuggestions([...o.scans], r);
    // The list compared before "That's right": what the photos suggest, so the person sees "To add" while reviewing.
    const previewItems: AckItem[] = s.items.map((x) => ({ id: x.id, typeKey: x.typeKey, kind: walkItem(x.typeKey)!.kind, label: x.label, ...(x.count !== undefined ? { count: x.count } : {}), ...(x.material ? { material: x.material } : {}), ...(x.drain !== undefined ? { drain: x.drain } : {}), ...(x.holding && HOLDING.includes(x.holding as Holding) ? { holding: x.holding as Holding } : {}) }));
    return { ...r, look: r.acknowledged ? lookFor(r.kind, r.acknowledged.items, place) : [], suggestions: s.items, rows: mergeRoomRows(s.items, { acknowledged: r.acknowledged?.items ?? [] }), photos: s.photos, labels: s.labels, issueSuggestions: o.prospect ? [] : s.issues, pending: s.pending, failed: s.failed, lookPreview: lookFor(r.kind, previewItems, place) };
  });
  const show = !!o.prospect || seesWins(o.role, o.viewerId, walk);
  const wins = show ? walkEasyWins(walk, place, { inspection: o.inspection ?? null }) : null;
  const setOn = dayText(walk.rooms.map((r) => r.acknowledged?.at).filter((x): x is string => !!x).sort()[0] ?? walk.startedAt, o.timeZone);
  const summary = handoffSummary(walk, { setOn, viewerId: o.viewerId ?? "", place, ...(wins ? { easyWinCount: wins.length } : {}) });
  const completion = walkCompletion(rooms);
  return {
    walk, rooms, map: placeMap(walk), progress: progressLine(walk), summary, easyWins: wins,
    prepBanner: walk.prep && !walk.handedOffAt && o.viewerId === walk.startedBy ? `Prepping ${o.orgName} for the owner` : null,
    // The preparer's next step (hand walk A7): the hand-off words are the owner's; the preparer needs "review it and send it".
    prepNext: walk.prep && !walk.handedOffAt && o.viewerId === walk.startedBy && o.prepReviewTo ? { label: "Review it and send it to the owner", to: o.prepReviewTo, line: `You set this up for the owner. Next: look at exactly what they'll see, then send it or pay for it.` } : null,
    canTakeOver: !!walk.prep && !walk.handedOffAt && !!o.offerForViewer && o.viewerId !== walk.startedBy,
    completion, prepProgress: walk.prep ? prepProgress(completion, { ...(o.orgKind ? { orgKind: o.orgKind } : {}) }) : null,
    sheet: placeSheet(walk, { placeName: o.placeName, setOn }), placeName: o.placeName, setOn,
  };
}
